import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'
import { objectsToArray } from 'functions/utilities'

import api from 'api'

const FETCH_REQUEST = 'realhub/providerZone/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/providerZone/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/providerZone/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/providerZone/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/providerZone/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/providerZone/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/providerZone/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/providerZone/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/providerZone/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/providerZone/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/providerZone/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/providerZone/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  loaded: false,
  loadedIds: [],
  loading: false,
  updating: false,
  errors: [],
}

// Action
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadProviderZones(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/admin/provider_zones.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_ZONE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadProviderZone(providerZoneId, options){
  return (dispatch, getState) => {
    const state = getState()
    const loadedIds = [...state.providerZones.loadedIds]
    if (!loadedIds.includes(providerZoneId)){
      loadedIds.push(providerZoneId)
    }
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/admin/provider_zones/${providerZoneId}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_ZONE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createProviderZone(providerZone, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      provider_zone: providerZone,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/admin/provider_zones.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_ZONE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess(normalizedJson))

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateProviderZone(providerZone, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      provider_zone: providerZone,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/provider_zones/${providerZone.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_ZONE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess(normalizedJson))

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteProviderZoneAgencyPrices(providerZone, options, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch, getState) => {
    dispatch(deleteRequest())

    const promise = api(`/admin/provider_zones/${providerZone.id}/delete_agency_prices.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Delete Agency Prices from Redux Store
        const { providerZoneAgencyPrices } = getState().entities
        const array = objectsToArray(providerZoneAgencyPrices)
        array.forEach((agencyPrice) => {
          if (agencyPrice.provider_zone_id === json.id){
            const normalizedJson = normalize(agencyPrice, Schemas.PROVIDER_ZONE_AGENCY_PRICE)
            dispatch(deleteEntity(normalizedJson))
          }
        })

        dispatch(deleteSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

export function deleteProviderZoneSuburbs(providerZone, options, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch, getState) => {
    dispatch(deleteRequest())

    const promise = api(`/admin/provider_zones/${providerZone.id}/delete_suburbs.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Delete Suburbs from Redux Store
        const { providerZoneSuburbs } = getState().entities
        const array = objectsToArray(providerZoneSuburbs)
        array.forEach((zoneSuburb) => {
          if (zoneSuburb.provider_zone_id === json.id){
            const normalizedJson = normalize(zoneSuburb, Schemas.PROVIDER_ZONE_SUBURB)
            dispatch(deleteEntity(normalizedJson))
          }
        })

        dispatch(deleteSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return Object.assign({}, state, {
        loading: true,
        loadedIds: action.loadedIds || state.loadedIds,
      })
    case FETCH_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
        loaded: true,
        errors: [],
      })
    case FETCH_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        loaded: false,
        errors: action.errors,
      })
    case CREATE_REQUEST:
      return Object.assign({}, state, {
        creating: true,
      })
    case CREATE_SUCCESS:
      return Object.assign({}, state, {
        creating: false,
        errors: [],
      })
    case CREATE_FAILURE:
      return Object.assign({}, state, {
        creating: false,
        errors: action.errors,
      })
    case UPDATE_REQUEST:
      return Object.assign({}, state, {
        updating: true,
      })
    case UPDATE_SUCCESS:
      return Object.assign({}, state, {
        updating: false,
        errors: [],
      })
    case UPDATE_FAILURE:
      return Object.assign({}, state, {
        updating: false,
        errors: [],
      })
    case DELETE_REQUEST:
      return Object.assign({}, state, {
        deleting: true,
      })
    case DELETE_SUCCESS:
      return Object.assign({}, state, {
        deleting: false,
        result: action.result,
        errors: [],
      })
    case DELETE_FAILURE:
      return Object.assign({}, state, {
        deleting: false,
        errors: action.errors,
      })
    default:
      return state
  }
}
